import { cva, type VariantProps } from 'class-variance-authority'
import { AlarmClock } from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  ProjectState,
  CardTypeKey,
  ClientStage,
  EffortKey,
  PriorityKey,
  ProjectTypeKey,
  StatusKey,
  TeamFunctionKey,
} from '@/types/entities'
import { latenessLabel, type Lateness } from '@/features/cards/lateness'

/**
 * The brand layer sits between shadcn primitives and features.
 *
 * Convention (see CLAUDE.md): composites take exactly three variant axes —
 * `tone`, `size`, `emphasis`. A fourth axis means you need a second component.
 * Composites also never accept a domain entity: this takes a `tone`, not a `Card`.
 */
const toneVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap font-medium transition-colors dur-fast',
  {
    variants: {
      tone: {
        neutral: '',
        brand: '',
        accent: '',
        success: '',
        warning: '',
        danger: '',
        info: '',
      },
      emphasis: {
        soft: '',
        solid: '',
        outline: 'bg-transparent border',
      },
      // Always smaller than any heading on the page — these are metadata, not content.
      // micro/tiny are custom (no Tailwind equivalent) precisely so badge text can be tuned
      // on its own, decoupled from body/heading sizes.
      size: {
        xs: 'h-5 rounded-sm px-2 text-micro',
        sm: 'h-6 rounded-sm px-2.5 text-tiny',
        md: 'h-7 rounded-md px-3 text-tiny',
      },
    },
    compoundVariants: [
      { tone: 'neutral', emphasis: 'soft', class: 'bg-neutral-soft text-text-secondary' },
      { tone: 'brand', emphasis: 'soft', class: 'bg-brand-soft text-brand-dark' },
      { tone: 'accent', emphasis: 'soft', class: 'bg-brand-accent-soft text-brand-accent-dark' },
      { tone: 'success', emphasis: 'soft', class: 'bg-success-soft text-success' },
      { tone: 'warning', emphasis: 'soft', class: 'bg-warning-soft text-warning' },
      { tone: 'danger', emphasis: 'soft', class: 'bg-danger-soft text-danger' },
      { tone: 'info', emphasis: 'soft', class: 'bg-info-soft text-info' },

      { tone: 'neutral', emphasis: 'solid', class: 'bg-text-secondary text-text-inverse' },
      { tone: 'brand', emphasis: 'solid', class: 'bg-brand text-text-inverse' },
      { tone: 'accent', emphasis: 'solid', class: 'bg-brand-accent text-text-inverse' },
      { tone: 'success', emphasis: 'solid', class: 'bg-success text-text-inverse' },
      { tone: 'warning', emphasis: 'solid', class: 'bg-warning text-text-inverse' },
      { tone: 'danger', emphasis: 'solid', class: 'bg-danger text-text-inverse' },
      { tone: 'info', emphasis: 'solid', class: 'bg-info text-text-inverse' },

      // A full-saturation border was as loud as `solid`, which inverted the hierarchy an
      // outline is supposed to sit under. The border is now a tint of the tone; only the
      // text carries full colour, so it still scans by colour without shouting.
      { tone: 'neutral', emphasis: 'outline', class: 'border-border-strong text-text-secondary' },
      { tone: 'brand', emphasis: 'outline', class: 'border-brand/30 text-brand' },
      { tone: 'accent', emphasis: 'outline', class: 'border-brand-accent/30 text-brand-accent' },
      { tone: 'success', emphasis: 'outline', class: 'border-success/30 text-success' },
      { tone: 'warning', emphasis: 'outline', class: 'border-warning/35 text-warning' },
      { tone: 'danger', emphasis: 'outline', class: 'border-danger/30 text-danger' },
      { tone: 'info', emphasis: 'outline', class: 'border-info/30 text-info' },
    ],
    defaultVariants: { tone: 'neutral', emphasis: 'soft', size: 'sm' },
  },
)

export type Tone = NonNullable<VariantProps<typeof toneVariants>['tone']>

export interface ToneBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof toneVariants> {}

export function ToneBadge({ tone, emphasis, size, className, ...props }: ToneBadgeProps) {
  return <span className={cn(toneVariants({ tone, emphasis, size }), className)} {...props} />
}

// --- domain mappings -------------------------------------------------------
// These live here rather than in feature code so status colour is decided once.

const STATUS_TONE: Record<StatusKey, Tone> = {
  todo: 'neutral',
  in_progress: 'brand',
  blocked: 'danger',
  in_review: 'warning',
  done: 'success',
}

/** Work Item v1 stage names. The keys stay as stored; only the words changed. */
const STATUS_LABEL: Record<StatusKey, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  in_review: 'Approval pending',
  done: 'Completed',
}

/**
 * The stage, when the stage is a stage.
 *
 * `status` is typed `StatusKey` and the database has rows where it is null — work created by
 * an accept-request path that omitted it. A type that promises non-null does not make the row
 * non-null; it only stops anyone checking.
 *
 * Rendering "Unknown" is the honest answer. Rendering nothing produced the empty grey badge
 * that made this hard to spot, and reading a label off the miss is what actually crashed the
 * work item drawer.
 */
export function StatusBadge({
  status,
  size = 'sm',
  className,
  lateness = 'none',
}: {
  status: StatusKey
  size?: VariantProps<typeof toneVariants>['size']
  className?: string
  /**
   * Whether the work is behind. It changes the COLOUR, never the words.
   *
   * The stage is what the badge says; late is how it says it. A separate "Backlog" pill beside
   * the real stage read as a sixth stage and hid the actual one — which is exactly the
   * confusion that led to a job rewriting the stage to match the pill.
   */
  lateness?: Lateness
}) {
  const late = lateness !== 'none'
  return (
    <ToneBadge
      tone={late ? 'danger' : (STATUS_TONE[status] ?? 'neutral')}
      size={size}
      className={className}
      title={latenessLabel(lateness) ?? undefined}
    >
      {statusLabel(status)}
    </ToneBadge>
  )
}

const PRIORITY_TONE: Record<PriorityKey, Tone> = {
  none: 'neutral',
  low: 'info',
  medium: 'brand',
  high: 'warning',
  urgent: 'danger',
}

const PRIORITY_LABEL: Record<PriorityKey, string> = {
  none: 'No priority',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
}

export function PriorityPill({
  priority,
  size = 'xs',
  className,
}: {
  priority: PriorityKey
  size?: VariantProps<typeof toneVariants>['size']
  className?: string
}) {
  if (priority === 'none') return null
  return (
    <ToneBadge tone={PRIORITY_TONE[priority]} size={size} className={className}>
      {PRIORITY_LABEL[priority]}
    </ToneBadge>
  )
}

const CARD_TYPE_TONE: Record<CardTypeKey, Tone> = {
  task: 'neutral',
  bug: 'danger',
  feature: 'accent',
  request: 'brand',
  question: 'info',
}

const CARD_TYPE_LABEL: Record<CardTypeKey, string> = {
  task: 'Task',
  bug: 'Bug',
  feature: 'Feature',
  request: 'Request',
  question: 'Question',
}

export function CardTypeBadge({
  cardType,
  size = 'xs',
  className,
}: {
  cardType: CardTypeKey
  size?: VariantProps<typeof toneVariants>['size']
  className?: string
}) {
  return (
    <ToneBadge tone={CARD_TYPE_TONE[cardType]} emphasis="outline" size={size} className={className}>
      {CARD_TYPE_LABEL[cardType]}
    </ToneBadge>
  )
}

const PROJECT_TYPE_TONE: Record<ProjectTypeKey, Tone> = {
  client: 'brand',
  pre_sales: 'accent',
  pilot: 'warning',
  implementation: 'brand',
  cost_improvement: 'info',
  hypercare: 'info',
  post_sales: 'neutral',
  additional_requirements: 'neutral',
  internal: 'success',
}

const PROJECT_TYPE_LABEL: Record<ProjectTypeKey, string> = {
  client: 'Client Project',
  pre_sales: 'Pre-sales',
  pilot: 'Pilot',
  implementation: 'Implementation',
  cost_improvement: 'Cost improvement',
  hypercare: 'Hypercare',
  post_sales: 'Post-sales support',
  additional_requirements: 'Additional requirements',
  internal: 'Internal',
}

/** The engagement type. It comes off the card's PROJECT, never off the card. */
export function ProjectTypeBadge({
  type,
  size = 'xs',
  className,
}: {
  type: ProjectTypeKey
  size?: VariantProps<typeof toneVariants>['size']
  className?: string
}) {
  return (
    <ToneBadge tone={PROJECT_TYPE_TONE[type]} emphasis="outline" size={size} className={className}>
      {PROJECT_TYPE_LABEL[type]}
    </ToneBadge>
  )
}

const EFFORT_LABEL: Record<EffortKey, string> = { s: 'S', m: 'M', l: 'L' }
const EFFORT_TITLE: Record<EffortKey, string> = { s: 'Small', m: 'Medium', l: 'Large' }

export function EffortBadge({
  effort,
  size = 'xs',
  className,
}: {
  effort: EffortKey | null
  size?: VariantProps<typeof toneVariants>['size']
  className?: string
}) {
  if (!effort) return null
  return (
    <ToneBadge tone="neutral" emphasis="outline" size={size} className={cn('num', className)} title={`${EFFORT_TITLE[effort]} effort`}>
      {EFFORT_LABEL[effort]}
    </ToneBadge>
  )
}

/**
 * An overdue item that has not advanced since its target passed.
 *
 * Takes no props at all — whether an item IS backlog is decided by `isBacklog` in
 * `types/entities.ts`, which is the single copy of that rule. This only draws it.
 */
export function BacklogBadge({
  size = 'xs',
  className,
}: {
  size?: VariantProps<typeof toneVariants>['size']
  className?: string
}) {
  return (
    <ToneBadge
      tone="danger"
      emphasis="solid"
      size={size}
      className={className}
      title="Overdue and not advanced since its target passed"
    >
      <AlarmClock className="size-3" aria-hidden />
      Overdue
    </ToneBadge>
  )
}

/** Marks work that was, or is, backlog. Stays after the item moves, so lateness is not erased. */
export function BacklogTag({ className }: { className?: string }) {
  return (
    <ToneBadge
      tone="warning"
      emphasis="soft"
      size="xs"
      className={className}
      title="This item was overdue before it was started"
    >
      Backlog
    </ToneBadge>
  )
}

const TEAM_FUNCTION_TONE: Record<TeamFunctionKey, Tone> = {
  sales: 'accent',
  pre_sales: 'info',
  delivery: 'brand',
  post_sales: 'warning',
  internal: 'success',
}

const TEAM_FUNCTION_LABEL: Record<TeamFunctionKey, string> = {
  sales: 'Sales',
  pre_sales: 'Pre Sales',
  delivery: 'Delivery',
  post_sales: 'Post Sales',
  internal: 'Internal',
}

/** What a team does. An unset function renders nothing at all — never "Unknown". */
export function TeamFunctionBadge({
  teamFunction,
  size = 'xs',
  className,
}: {
  teamFunction: TeamFunctionKey | null
  size?: VariantProps<typeof toneVariants>['size']
  className?: string
}) {
  if (!teamFunction) return null
  return (
    <ToneBadge tone={TEAM_FUNCTION_TONE[teamFunction]} size={size} className={className}>
      {TEAM_FUNCTION_LABEL[teamFunction]}
    </ToneBadge>
  )
}

/**
 * Every function a team holds, as separate badges.
 *
 * Separate rather than one badge listing them: each function has its own tone, and a team
 * that both sells and delivers should read as two facts rather than one hyphenated label.
 */
export function TeamFunctionBadges({
  teamFunctions,
  size = 'xs',
  className,
}: {
  teamFunctions: TeamFunctionKey[]
  size?: VariantProps<typeof toneVariants>['size']
  className?: string
}) {
  if (teamFunctions.length === 0) return null
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1', className)}>
      {teamFunctions.map((fn) => (
        <TeamFunctionBadge key={fn} teamFunction={fn} size={size} />
      ))}
    </span>
  )
}

const CLIENT_STAGE_TONE: Record<ClientStage, Tone> = {
  pilot: 'warning',
  implementation: 'brand',
  post_sales: 'success',
}

const CLIENT_STAGE_LABEL: Record<ClientStage, string> = {
  pilot: 'Pilot',
  implementation: 'Implementation',
  post_sales: 'Post-sales',
}

export function ClientStageBadge({
  stage,
  size = 'sm',
  className,
}: {
  stage: ClientStage
  size?: VariantProps<typeof toneVariants>['size']
  className?: string
}) {
  return (
    <ToneBadge tone={CLIENT_STAGE_TONE[stage]} size={size} className={className}>
      {CLIENT_STAGE_LABEL[stage]}
    </ToneBadge>
  )
}

export function statusTone(status: StatusKey): Tone {
  return STATUS_TONE[status] ?? 'neutral'
}

/**
 * The label, never undefined.
 *
 * Callers chain onto this — `statusLabel(s).toLowerCase()` — and a map miss returning
 * undefined is what took the drawer down for a row whose status was never written.
 */
export function statusLabel(status: StatusKey | null | undefined): string {
  return (status && STATUS_LABEL[status]) || 'Unknown'
}

export {
  toneVariants,
  STATUS_LABEL,
  PRIORITY_LABEL,
  CARD_TYPE_LABEL,
  CLIENT_STAGE_LABEL,
  PROJECT_TYPE_LABEL,
  TEAM_FUNCTION_LABEL,
  EFFORT_LABEL,
  EFFORT_TITLE,
}

/**
 * A project's state.
 *
 * Empty is deliberately quiet — a project with no work yet is not a problem, and giving it a
 * loud badge would put a warning on every project the day it is opened.
 */
export const PROJECT_STATE_LABEL: Record<ProjectState, string> = {
  empty: 'Empty',
  ongoing: 'Ongoing',
  blocked: 'Blocked',
  completion_pending: 'Completion pending',
  completed: 'Completed',
}

const PROJECT_STATE_TONE: Record<ProjectState, Tone> = {
  empty: 'neutral',
  ongoing: 'brand',
  blocked: 'danger',
  completion_pending: 'warning',
  completed: 'success',
}

export function ProjectStateBadge({
  state,
  size = 'xs',
  className,
}: {
  state: ProjectState
  size?: VariantProps<typeof toneVariants>['size']
  className?: string
}) {
  return (
    <ToneBadge tone={PROJECT_STATE_TONE[state]} size={size} className={className}>
      {PROJECT_STATE_LABEL[state]}
    </ToneBadge>
  )
}
